/**
 * A Stimulus controller that automatically selects radio buttons when corresponding form inputs have values selected.
 *
 * This controller creates smart form behavior where selecting a value in a dropdown, text input, or other form element
 * automatically checks its associated radio button. This is particularly useful for forms with mutually exclusive
 * options where users need to first select a radio button and then fill in related details.
 *
 * Features:
 * - Automatically selects radio buttons when associated inputs have non-empty values
 * - Works on page load to handle pre-filled forms or validation errors
 * - Supports any form input type (select, text, email, etc.)
 * - Uses data attributes for flexible pairing of inputs with radio buttons
 *
 * Data Attributes:
 * - data-auto-select-radio-id: Unique identifier placed on radio buttons
 * - data-auto-select-radio-for: Reference to radio button ID, placed on associated inputs
 * - action="change->auto-select-radio#changeInput": Triggers when input value changes
 *
 * @example
 * HTML structure for mutually exclusive employee selection:
 *
 * <div data-controller="auto-select-radio">
 *   <!-- Option 1: Select all employees -->
 *   <label>
 *     <input type="radio" name="employees" value="all" checked>
 *     All employees
 *   </label>
 *
 *   <!-- Option 2: Select specific class -->
 *   <label>
 *     <input type="radio" name="employees" value="class"
 *            data-auto-select-radio-id="class_radio">
 *     This class only
 *   </label>
 *   <select data-auto-select-radio-for="class_radio"
 *           data-action="change->auto-select-radio#changeInput">
 *     <option value="">Choose class...</option>
 *     <option value="1">Management</option>
 *     <option value="2">Staff</option>
 *   </select>
 *
 *   <!-- Option 3: Select specific employee -->
 *   <label>
 *     <input type="radio" name="employees" value="employee"
 *            data-auto-select-radio-id="employee_radio">
 *     This employee only
 *   </label>
 *   <select data-auto-select-radio-for="employee_radio"
 *           data-action="change->auto-select-radio#changeInput">
 *     <option value="">Choose employee...</option>
 *     <option value="101">John Doe</option>
 *     <option value="102">Jane Smith</option>
 *   </select>
 * </div>
 *
 * Behavior:
 * - When user selects "Management" in the class dropdown, the "This class only" radio button is automatically checked
 * - When user selects "John Doe" in the employee dropdown, the "This employee only" radio button is automatically checked
 * - Works on initial page load if dropdowns already have selected values (useful for form validation errors or edit forms)
 */
import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  connect() {
    this.checkInitialState();
  }

  checkInitialState() {
    const inputs = this.element.querySelectorAll('[data-auto-select-radio-for]');

    inputs.forEach((input) => {
      this.selectRadio(input);
    });
  }

  changeInput(event) {
    this.selectRadio(event.target);
  }

  selectRadio(input) {
    if (input.value && input.value !== '') {
      const correspondingRadio = this.findCorrespondingRadio(input);

      if (correspondingRadio) {
        correspondingRadio.checked = true;
      }
    }
  }

  findCorrespondingRadio(dropdown) {
    const radioId = dropdown.dataset.autoSelectRadioFor;
    return radioId ? this.element.querySelector(`[data-auto-select-radio-id="${radioId}"]`) : null;
  }
}
